import React, { useState } from 'react';
import './InternshipApply.css';

const SOURCE_OPTIONS = [
  'Friend / Family Referral',
  'School / College Recommendation',
  'Google Search',
  'Social Media (Instagram / Facebook)',
  'YouTube',
  'LinkedIn',
  'WhatsApp',
  'Newspaper / Magazine',
  'Event / Hackathon',
  'ibots Website',
  'Other',
];

const FOUNDATION_COURSES = ['Embedded Systems', 'IoT', 'Robotics'];
const ADVANCED_COURSES = ['AI with RPi', 'Mechatronics', 'Drone Technology', '3D Printing'];

const TRACK_DATA = {
  week1: {
    title: '1-Week Internship — Track A (Foundational)',
    applyTitle: 'Apply — 1-Week Track A (Foundational)',
    duration: '1 Week',
    domains: '3 Domains',
    certificate: 'Certificate + Letter',
    domainOptions: ['Embedded Systems', 'IoT', 'Robotics'],
    selectText: 'Select 1 domain only',
    desc: 'An intensive beginner-level program focused on building core technical understanding through hands-on learning. Students work on simple practical modules and mini-projects to develop basic skills and confidence in hardware-based systems. Certificate and official internship letter provided upon completion.',
    hardware: [
      'Arduino & Microcontroller Programming (Embedded Systems)',
      'Sensor Integration & Circuit Design (IoT)',
      'Robotics Assembly & Motor Control (Robotics)',
      '3D Design Basics & Printing Workflow (3D Printing)',
    ],
    software: ['Embedded C Programming', 'IoT Protocols & Cloud Dashboard', 'Python for Robotics Control', 'AI & Image Processing Scripts'],
    trackOptions: ['Hardware Track'],
  },
  week2: {
    title: '2-Week Internship — Track B (Foundation & Advanced)',
    applyTitle: 'Apply — 2-Week Track B (Foundation & Advanced)',
    duration: '2 Weeks',
    domains: '7 Domains',
    certificate: 'Certificate + Letter',
    domainOptions: ['Embedded Systems', 'IoT', 'Robotics', 'AI with RPi', 'Mechatronics', 'Drone Technology', '3D Printing'],
    selectText: 'Select 1 domain only',
    desc: 'A deeper, skill-enhancing program designed for students with prior exposure. It focuses on complex system building, integration concepts, and advanced hands-on applications with industry-level project experience. Certificate and internship letter provided.',
    hardware: [
      'Embedded Systems & IoT Hardware Setup',
      'Robotics Assembly & Wiring',
      'Advanced Robotics Mechanics & Assembly',
      'Mechatronics Systems & Actuators',
      'Drone Frame Assembly & ESC Configuration',
      'STM32 Board & Peripheral Setup',
      'AI with Raspberry Pi Hardware Integration',
      'IIoT & AIoT Hardware Components',
      '3D Printing & Prototyping',
    ],
    software: ['IIoT & AIoT Protocols & Dashboards', 'ROS Basics & Robot Automation', 'Betaflight & PID Tuning Software', 'STM32 Firmware & HAL Programming'],
    trackOptions: ['Hardware Track'],
    showPriorBackground: true,
  },
  month1: {
    title: '1-Month Internship — Full Programme Track',
    applyTitle: 'Apply — 1-Month Full Programme Track',
    duration: '1 Month',
    domains: '9 Domains',
    certificate: 'Full Certificate',
    certificateNote: '+ Letter',
    extraStat: { value: 'Prototype', label: 'Project Dev.' },
    domainOptions: [
      { label: 'Embedded & IoT', standalone: false },
      { label: 'Robotics', standalone: false },
      { label: 'AI with RPI', standalone: false },
      { label: 'Advanced Robotics', standalone: false },
      { label: 'Mechatronics', standalone: false },
      { label: 'STM32 RMS', standalone: false },
      { label: 'Drone Technology', standalone: true },
      { label: '3D Printing', standalone: true },
    ],
    selectText: 'Choose any 2 domains as a combination',
    desc: 'A complete, immersive training program covering all levels of learning from fundamentals to advanced implementation. Students work on end-to-end system development, culminating in a fully functional real-world prototype with documentation and certification.',
    hardware: [
      'Embedded Systems & IoT Hardware Setup',
      'Robotics Assembly & Motor Control',
      'Advanced Robotics Mechanics & Assembly',
      'Mechatronics Systems & Actuators',
      'Drone Frame Assembly & ESC Configuration',
      'STM32 Board & Peripheral Integration',
      'AI with Raspberry Pi Hardware Projects',
      'IIoT & AIoT Systems & Components',
      '3D Printing & Prototype Development',
      'End-to-End Prototype Build & Documentation',
    ],
    software: ['Embedded C, Python & IoT Stack', 'AI, Computer Vision & IIoT/AIoT', 'STM32 Firmware & HAL Drivers', 'Prototype Build (Software Focus)'],
    trackOptions: ['Hardware Track'],
    isMonth: true,
  },
};

const errStyle = { color: '#e8394d', fontSize: '11px', marginTop: '4px' };

function InternshipApply({ track, onClose }) {
  const data = TRACK_DATA[track] || TRACK_DATA.week1;
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '', phone: '', email: '', institution: '',
    programme: '', startDate: '', trackPref: '', domain: '',
    level: '', skills: '', source: '', customSource: '',
  });
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const updated = { ...formData, [e.target.name]: e.target.value };
    if (e.target.name === 'trackPref') { updated.level = ''; updated.domain = ''; }
    if (e.target.name === 'level') { updated.domain = ''; }
    setFormData(updated);
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const toggleDomain = (label, isStandalone) => {
    if (isStandalone) {
      setSelectedDomains(prev => prev.includes(label) ? [] : [label]);
      return;
    }
    const standaloneSelected = selectedDomains.some(d =>
      data.domainOptions?.find(o => o.label === d && o.standalone)
    );
    if (standaloneSelected) return;
    setSelectedDomains(prev => {
      if (prev.includes(label)) return prev.filter(d => d !== label);
      if (prev.length >= 2) return prev;
      return [...prev, label];
    });
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!formData.fullName) newErrors.fullName = 'Full name is required';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.institution) newErrors.institution = 'Institution is required';
    if (!formData.trackPref) newErrors.trackPref = 'Please select a course';
    if (!formData.source) newErrors.source = 'Please tell us how you heard about ibots';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTimeout(() => {
        const firstError = document.querySelector('.error-field');
        if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    try {
      await fetch('http://localhost:5000/api/enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formType: 'internship-apply',
          fullName: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          institution: formData.institution,
          programme: formData.programme,
          startDate: formData.startDate,
          trackPref: formData.trackPref,
          level: formData.level,
          selectedDomains: selectedDomains,
          domain: formData.domain,
          skills: formData.skills,
          source: formData.source === 'Other' ? formData.customSource : formData.source,
        }),
      });
      try {
        await fetch('http://localhost:5000/api/centre', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name:         formData.fullName,
            type:         'College',
            phone:        formData.phone,
            email:        formData.email,
            institution:  formData.institution,
            grade:        formData.programme || '',
            mode:         'Offline',
            formType:     ({
              'week1':  '1-Week Internship',
              'week2':  '2-Week Internship',
              'month1': '1-Month Internship',
            }[track] || 'Internship'),
            level:        formData.level || '',
            course:       formData.domain || selectedDomains.join(', ') || '',
            startDate:    formData.startDate || '',
            skills:       formData.skills || '',
            source:       formData.source === 'Other' ? formData.customSource : formData.source,
          }),
        });
      } catch (err) {
        console.log('Dashboard error:', err);
      }
      setSubmitted(true);
    } catch (error) {
      alert('Something went wrong. Please try again.');
    }
  };

  const isHardware = formData.trackPref.includes('Hardware');
  const coursesForLevel = formData.level === 'foundation' ? FOUNDATION_COURSES : formData.level === 'advanced' ? ADVANCED_COURSES : [];

  if (submitted) {
    return (
      <div className="ia__overlay" onClick={onClose}>
        <div className="ia__box" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', textAlign: 'center', gap: '16px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#dcfce7', color: '#16a34a', fontSize: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>✓</div>
          <h2 style={{ color: '#0d2e6e', fontWeight: 900, margin: 0 }}>Thank You for Reaching Out to ibots!</h2>
          <p style={{ color: '#64748b', margin: 0, maxWidth: '300px', lineHeight: '1.6' }}>We have received your internship application. Our team will get in touch with you within <strong>24 hours</strong> to confirm the next steps.</p>
          <button className="ia__submit" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ia__overlay" onClick={onClose}>
      <div className="ia__box" onClick={(e) => e.stopPropagation()}>

        <button className="ia__close" onClick={onClose}>✕ Close</button>
        <h2 className="ia__title">{data.title}</h2>

        {/* Stats */}
        <div className={`ia__stats ${data.isMonth ? 'ia__stats--4' : ''}`}>
          <div className="ia__stat">
            <p className="ia__stat-value">{data.duration}</p>
            <p className="ia__stat-label">Duration</p>
          </div>
          <div className="ia__stat">
            <p className="ia__stat-value">{data.domains}</p>
            <p className="ia__stat-label">Offered</p>
          </div>
          {data.extraStat && (
            <div className="ia__stat">
              <p className="ia__stat-value ia__stat-value--blue">{data.extraStat.value}</p>
              <p className="ia__stat-label">{data.extraStat.label}</p>
            </div>
          )}
          <div className="ia__stat">
            <p className="ia__stat-value ia__stat-value--blue">{data.certificate}</p>
            {data.certificateNote && <p className="ia__stat-note">{data.certificateNote}</p>}
            <p className="ia__stat-label">Provided</p>
          </div>
        </div>

        {/* Course info */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div style={{ flex: 1, minWidth: '180px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px' }}>
            <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 700, color: '#166534', textTransform: 'uppercase' }}>Foundation Courses</p>
            {FOUNDATION_COURSES.map((c, i) => (
              <p key={i} style={{ margin: '3px 0', fontSize: '12px', color: '#15803d' }}>✓ {c}</p>
            ))}
          </div>
          {track !== 'week1' && (
            <div style={{ flex: 1, minWidth: '180px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase' }}>Advanced Courses</p>
              {ADVANCED_COURSES.map((c, i) => (
                <p key={i} style={{ margin: '3px 0', fontSize: '12px', color: '#1d4ed8' }}>✓ {c}</p>
              ))}
            </div>
          )}
        </div>

        {data.isMonth ? (
          <div className="ia__month-bars">
            <div className="ia__month-bar ia__month-bar--gray">Choose <u>any 2 domains</u> as a combination</div>
            <div className="ia__month-bar ia__month-bar--dark"><strong>Drone Technology</strong><span className="ia__bar-sep">|</span>Standalone single domain only — cannot be combined</div>
            <div className="ia__month-bar ia__month-bar--navy"><strong>3D Printing</strong><span className="ia__bar-sep">|</span>Standalone single domain only — cannot be combined</div>
          </div>
        ) : (
          <div className="ia__domain-bar">
            <span className="ia__domain-label">Select <u>1 domain</u> only</span>
            {(track === 'week1' ? FOUNDATION_COURSES : [...FOUNDATION_COURSES, ...ADVANCED_COURSES]).map((d, i) => (
              <span key={i} className="ia__domain-chip">{d}</span>
            ))}
          </div>
        )}

        <p className="ia__desc">{data.desc}</p>

        <div className="ia__tracks">
          <div className="ia__track-card" style={{ width: '100%' }}>
            <p className="ia__track-title">HARDWARE TRACK</p>
            <ul className="ia__track-list" style={{ columns: 2, columnGap: '24px' }}>
              {data.hardware.map((item, i) => (<li key={i}><span className="ia__check">✓</span> {item}</li>))}
            </ul>
          </div>
        </div>

        <h3 className="ia__form-title">{data.applyTitle}</h3>

        <div className="ia__form">
          <div className="ia__row">
            <div className="ia__group">
              <label className="ia__label">Full Name *</label>
              <input type="text" name="fullName" value={formData.fullName}
                onChange={handleChange} placeholder="Enter full name" className="ia__input" />
              {errors.fullName && <p style={errStyle} className="error-field">{errors.fullName}</p>}
            </div>
            <div className="ia__group">
              <label className="ia__label">Phone *</label>
              <input type="text" name="phone" value={formData.phone}
                onChange={handleChange} placeholder="+91 XXXXX XXXXX" className="ia__input" />
              {errors.phone && <p style={errStyle} className="error-field">{errors.phone}</p>}
            </div>
          </div>

          <div className="ia__row">
            <div className="ia__group">
              <label className="ia__label">Email *</label>
              <input type="email" name="email" value={formData.email}
                onChange={handleChange} placeholder="you@example.com" className="ia__input" />
              {errors.email && <p style={errStyle} className="error-field">{errors.email}</p>}
            </div>
            <div className="ia__group">
              <label className="ia__label">Institution *</label>
              <input type="text" name="institution" value={formData.institution}
                onChange={handleChange} placeholder="College name" className="ia__input" />
              {errors.institution && <p style={errStyle} className="error-field">{errors.institution}</p>}
            </div>
          </div>

          <div className="ia__row">
            <div className="ia__group">
              <label className="ia__label">Programme & Year</label>
              <input type="text" name="programme" value={formData.programme}
                onChange={handleChange}
                placeholder={data.isMonth ? "e.g. B.E. ECE, 4th Year" : "e.g. B.E. ECE, 3rd Year"}
                className="ia__input" />
            </div>
            <div className="ia__group">
              <label className="ia__label">Preferred Start Date</label>
              <input type="text" name="startDate" value={formData.startDate}
                onChange={handleChange} placeholder="e.g. June 2025" className="ia__input" />
            </div>
          </div>

          <div className="ia__group ia__group--full">
            <label className="ia__label">Course Preference *</label>
            <select name="trackPref" value={formData.trackPref}
              onChange={handleChange} className="ia__input ia__select">
              <option value="">Choose your course...</option>
              {data.trackOptions.map((t, i) => (<option key={i} value={t}>{t}</option>))}
            </select>
            {errors.trackPref && <p style={errStyle} className="error-field">{errors.trackPref}</p>}
          </div>

          {/* Level selection for Hardware track (non-month) */}
          {!data.isMonth && isHardware && (
            <div className="ia__group ia__group--full">
              <label className="ia__label">Select Level *</label>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '6px' }}>
                <div
                  onClick={() => handleChange({ target: { name: 'level', value: 'foundation' } })}
                  style={{
                    flex: 1, minWidth: '140px',
                    border: `2px solid ${formData.level === 'foundation' ? '#1a237e' : '#e2e8f0'}`,
                    borderRadius: '10px', padding: '12px 16px', cursor: 'pointer',
                    background: formData.level === 'foundation' ? '#e8eeff' : '#f8fafc',
                  }}>
                  <p style={{ margin: 0, fontWeight: 700, color: '#0d2e6e', fontSize: '14px' }}>Foundation</p>
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>Embedded Systems · IoT · Robotics</p>
                </div>
                {track !== 'week1' && (
                  <div
                    onClick={() => handleChange({ target: { name: 'level', value: 'advanced' } })}
                    style={{
                      flex: 1, minWidth: '140px',
                      border: `2px solid ${formData.level === 'advanced' ? '#1a237e' : '#e2e8f0'}`,
                      borderRadius: '10px', padding: '12px 16px', cursor: 'pointer',
                      background: formData.level === 'advanced' ? '#e8eeff' : '#f8fafc',
                    }}>
                    <p style={{ margin: 0, fontWeight: 700, color: '#0d2e6e', fontSize: '14px' }}>Advanced</p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>AI with RPi · Mechatronics · Drone · 3D Printing</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Course selection based on level */}
          {!data.isMonth && isHardware && formData.level && (
            <div className="ia__group ia__group--full">
              <label className="ia__label">Select Course * <span className="ia__choose-badge">Choose 1 only</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                {coursesForLevel.map((course, i) => (
                  <div
                    key={i}
                    onClick={() => handleChange({ target: { name: 'domain', value: course } })}
                    style={{
                      border: `2px solid ${formData.domain === course ? '#1a237e' : '#e2e8f0'}`,
                      borderRadius: '8px', padding: '8px 16px', cursor: 'pointer',
                      background: formData.domain === course ? '#e8eeff' : '#f8fafc',
                      fontSize: '13px', fontWeight: 600,
                      color: formData.domain === course ? '#0d2e6e' : '#475569',
                    }}>
                    {course}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Month domain selection */}
          {data.isMonth && formData.trackPref && (
            <div className="ia__group ia__group--full">
              <label className="ia__label">Select Domain(s) * <span className="ia__choose-badge">Choose 2 · or Drone as standalone</span></label>
              <div className="ia__checkbox-grid">
                {data.domainOptions.map((d, i) => {
                  const isChecked = selectedDomains.includes(d.label);
                  const standaloneSelected = selectedDomains.some(sel => data.domainOptions.find(o => o.label === sel && o.standalone));
                  const isDisabled = !isChecked && ((d.standalone && selectedDomains.length > 0 && !standaloneSelected) || (!d.standalone && standaloneSelected) || (!d.standalone && !isChecked && selectedDomains.filter(s => !data.domainOptions.find(o => o.label === s && o.standalone)).length >= 2));
                  return (
                    <div key={i} className={`ia__checkbox-item ${d.standalone ? 'ia__checkbox-item--full' : ''} ${isChecked ? 'ia__checkbox-item--checked' : ''} ${isDisabled ? 'ia__checkbox-item--disabled' : ''}`}
                      onClick={() => !isDisabled && toggleDomain(d.label, d.standalone)}>
                      <input type="checkbox" checked={isChecked} readOnly className="ia__checkbox" />
                      <span>{d.label}</span>
                      {d.standalone && (<span className={`ia__standalone-badge ${isChecked ? 'ia__standalone-badge--active' : ''}`}>Standalone only</span>)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="ia__group ia__group--full">
            <label className="ia__label">
              {data.isMonth ? 'Preferred Prototype / Project Idea (optional)' : data.showPriorBackground ? 'Prior Technical Background (required for Track B)' : 'Skills / Prior Experience'}
            </label>
            <textarea name="skills" value={formData.skills} onChange={handleChange}
              placeholder={data.isMonth ? 'Any project idea or domain focus for prototype development...' : data.showPriorBackground ? 'List relevant skills, courses completed, or projects done...' : 'Mention relevant skills and background...'}
              className="ia__input ia__textarea" />
          </div>

          <div className="ia__group ia__group--full">
            <label className="ia__label">How did you hear about ibots? *</label>
            <select name="source" value={formData.source}
              onChange={handleChange} className="ia__input ia__select">
              <option value="">Select...</option>
              {SOURCE_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
            {errors.source && <p style={errStyle} className="error-field">{errors.source}</p>}
            {formData.source === 'Other' && (
              <input type="text" name="customSource" value={formData.customSource}
                onChange={handleChange} placeholder="Please tell us how you found us..."
                className="ia__input" style={{ marginTop: '8px' }} />
            )}
          </div>

          <button className="ia__submit" onClick={handleSubmit}>
            Submit Application →
          </button>
        </div>
      </div>
    </div>
  );
}

export default InternshipApply;
